import React, { useEffect, useRef } from 'react';
import { LEVELS } from './PixelField';
type Props = {
  className?: string;
  /** Same pixel size as the footer field */
  cell?: number;
};

// Network lattice: a node every NODE_GAP cells, joined by faint links
const NODE_GAP = 4;
// Overall intensity — quiet, sits under the headline
const GAIN = 0.75;
// How quickly each pixel eases toward its new brightness (lower = calmer)
const EASE = 0.06;
// Cursor only bends the existing pattern — radius and strength in cells
const WARP_RADIUS = 18;
const WARP_AMOUNT = 3.6;
const WARP_FOLLOW = 0.05;
const WARP_FADE = 0.03;

/**
 * The footer's pixel styling arranged as a calm neural network. Nodes sit on a sparse lattice;
 * slow waves of "activation" drift across it from wandering origins, softly lighting nodes and
 * the links between neighbours as they pass — like signals moving through a model.
 * Every pixel eases toward its brightness, so nothing pops. The cursor never draws anything:
 * it only bends the field nearby, like a lens. Fades to black at the bottom; clear behind the headline.
 */
export function HeroPixelMind({
  className = '',
  cell = 14
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !host || !ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let visible = true;
    let shown = new Float32Array(0);
    const start = performance.now();
    const target = {
      x: 0,
      y: 0
    };
    const lens = {
      x: 0,
      y: 0
    };
    let hovering = false;
    let strength = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const {
        width,
        height
      } = host.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / cell);
      rows = Math.ceil(height / cell);
      shown = new Float32Array(cols * rows);
    };

    // Soft activation: three slow wavefronts expanding from origins that wander on long loops
    const activation = (x: number, y: number, t: number) => {
      let a = 0;
      for (let i = 0; i < 3; i++) {
        const ox = cols * (0.5 + 0.42 * Math.sin(t * 0.05 + i * 2.1));
        const oy = rows * (0.45 + 0.3 * Math.cos(t * 0.04 + i * 1.7));
        const d = Math.hypot(x - ox, y - oy);
        const front = (t * 3.2 + i * 17) % 46; // radius of the travelling ring, in cells
        a += Math.exp(-Math.pow((d - front) / 3.5, 2)) * (1 - front / 46);
      }
      // A gentle resting hum so the network never goes fully dark
      a += 0.18 * (0.5 + 0.5 * Math.sin(x * 0.31 + y * 0.23 + t * 0.35));
      return Math.min(1, a);
    };

    // Fades to black toward the bottom; clear pocket behind the headline
    const mask = (x: number, y: number) => {
      const u = x / cols;
      const v = y / rows;
      const fade = Math.max(0, Math.min(1, (0.9 - v) / 0.45));
      const pocket = Math.min(1, Math.hypot((u - 0.5) / 0.3, (v - 0.36) / 0.18));
      return fade * fade * (0.12 + pocket * 0.88);
    };
    const draw = (now: number) => {
      const t = reduce ? 8 : (now - start) / 1000;
      lens.x += (target.x - lens.x) * WARP_FOLLOW;
      lens.y += (target.y - lens.y) * WARP_FOLLOW;
      strength += ((hovering && !reduce ? 1 : 0) - strength) * WARP_FADE;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const m = mask(x, y);
          const i = y * cols + x;
          let goal = 0;
          if (m > 0.01) {
            // Lens: sample the network from a point bent toward the cursor
            let sx = x;
            let sy = y;
            if (strength > 0.001) {
              const dx = x - lens.x;
              const dy = y - lens.y;
              const d = Math.hypot(dx, dy);
              if (d < WARP_RADIUS && d > 0.0001) {
                const f = Math.pow(1 - d / WARP_RADIUS, 2) * strength * WARP_AMOUNT;
                sx -= dx / d * f;
                sy -= dy / d * f;
              }
            }
            const gx = Math.round(sx);
            const gy = Math.round(sy);
            const onRow = (gy % NODE_GAP + NODE_GAP) % NODE_GAP === 0;
            const onCol = (gx % NODE_GAP + NODE_GAP) % NODE_GAP === 0;
            if (onRow && onCol) {
              // Node
              goal = 0.25 + activation(sx, sy, t) * 0.9;
            } else if (onRow || onCol) {
              // Link between two nodes — lit only where both ends are active
              const ax = onRow ? Math.floor(gx / NODE_GAP) * NODE_GAP : gx;
              const ay = onRow ? gy : Math.floor(gy / NODE_GAP) * NODE_GAP;
              const bx = onRow ? ax + NODE_GAP : gx;
              const by = onRow ? gy : ay + NODE_GAP;
              goal = Math.min(activation(ax, ay, t), activation(bx, by, t)) * 0.7;
            }
            goal *= GAIN * m;
          }

          // Ease toward the goal so brightness changes glide rather than step
          shown[i] += (goal - shown[i]) * (reduce ? 1 : EASE);
          const level = LEVELS[Math.min(LEVELS.length - 1, Math.floor(shown[i] * LEVELS.length))];
          if (level > 0) {
            // Blend between steps by the fractional part for extra smoothness
            ctx.globalAlpha = level * Math.min(1, shown[i] * LEVELS.length - Math.floor(shown[i] * LEVELS.length) + 0.6);
            ctx.fillRect(x * cell, y * cell, cell - 1, cell - 1);
          }
        }
      }
      ctx.globalAlpha = 1;
      if (visible && !reduce) raf = requestAnimationFrame(draw);
    };
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        hovering = false;
        return;
      }
      target.x = (e.clientX - rect.left) / cell;
      target.y = (e.clientY - rect.top) / cell;
      if (!hovering && strength < 0.01) {
        lens.x = target.x;
        lens.y = target.y;
      }
      hovering = true;
    };
    const onLeave = () => {
      hovering = false;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible) raf = requestAnimationFrame(draw);
    });
    const ro = new ResizeObserver(() => {
      resize();
      if (reduce) draw(performance.now());
    });
    resize();
    ro.observe(host);
    io.observe(host);
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    document.addEventListener('pointerleave', onLeave);
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [cell]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`pixel-anim pointer-events-none absolute inset-0 ${className}`} data-id="element-192" data-mp="u36795d52i03" />;
}